// ---------------------------------------------------------------------------
// DnD Email Editor V2 – React Canvas (no iframe)
//
// Renders each layer/element as a styled React component. Applies model
// styles inline so the canvas looks like the actual email. Includes
// layer/element toolbars, hover/select states, inline WYSIWYG editing,
// and drag-and-drop indicators.
// ---------------------------------------------------------------------------

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from 'react';
import {
  GripVertical,
  Copy,
  Lock,
  LockOpen,
  Trash2,
  ArrowLeft,
  Plus,
  ImageIcon,
  Video,
  Pencil,
} from 'lucide-react';
import type {
  Layer,
  Element as DndElement,
  GlobalStyles,
  WrapperSettings,
  Spacing,
  BorderSides,
  ColumnsLayer,
  TextLayer,
  ImageLayer,
  ButtonLayer,
  DividerLayer,
  SpacerLayer,
  HtmlLayer,
  SocialLayer,
  SocialLink,
  ColumnsLayout,
  VideoLayer,
  CountdownLayer,
  Column,
  TextElement,
  ImageElement,
  ButtonElement,
  DividerElement,
  SpacerElement,
  HtmlElement,
  SocialElement,
  ElementType,
} from './dnd-model';
import { hasSpacing, hasVisibleBorder } from './dnd-model';
import type { SelectionState } from './useDndEditor';
import { stripPageAffectingTags } from './dnd-sanitize';
import type { BlockTier } from './dnd-blocks';
import { getBlocksByTier } from './dnd-blocks';
import { SOCIAL_SVG_PATHS } from './dnd-social-icons';
import { getColumnWidths, getColumnPixelWidths, changeColumnsLayout } from './dnd-columns';
import { inlineContentStyles } from './dnd-typography';
import { InlineTextEditor } from './InlineTextEditor';
import type { SocialModalResult } from './DndSocialModal';

// ---- Constants ------------------------------------------------------------

// Blocks that can live inside a column (element-tier + both-tier), ordered
// with the most-used content blocks first.
const ELEMENT_BLOCK_ORDER = ['text', 'image', 'button', 'divider', 'spacer', 'html', 'social'];
const ELEMENT_BLOCKS = [...getBlocksByTier('element')].sort(
  (a, b) => ELEMENT_BLOCK_ORDER.indexOf(a.type) - ELEMENT_BLOCK_ORDER.indexOf(b.type),
);

// ---- Style helpers --------------------------------------------------------

function spacingToCss(s: Spacing): string {
  return `${s.top}px ${s.right}px ${s.bottom}px ${s.left}px`;
}

function borderSideToCss(b: { width: number; style: string; color: string }): string | undefined {
  if (b.style === 'none' || b.width === 0) return undefined;
  return `${b.width}px ${b.style} ${b.color}`;
}

function bordersToStyle(b: BorderSides): CSSProperties {
  const s: CSSProperties = {};
  const t = borderSideToCss(b.top); if (t) s.borderTop = t;
  const r = borderSideToCss(b.right); if (r) s.borderRight = r;
  const bo = borderSideToCss(b.bottom); if (bo) s.borderBottom = bo;
  const l = borderSideToCss(b.left); if (l) s.borderLeft = l;
  if (b.radius && b.radius > 0) s.borderRadius = b.radius;
  return s;
}

function layerBaseStyle(layer: Layer): CSSProperties {
  const s: CSSProperties = {
    padding: spacingToCss(layer.padding),
    ...bordersToStyle(layer.border),
  };
  if (layer.bgColor) s.backgroundColor = layer.bgColor;
  if (layer.textColor) s.color = layer.textColor;
  if (layer.bgImage) {
    s.backgroundImage = `url(${layer.bgImage})`;
    s.backgroundRepeat = layer.bgRepeat || 'no-repeat';
    s.backgroundPosition = layer.bgPosition || 'center center';
    s.backgroundSize = layer.bgSize || 'cover';
  }
  return s;
}

// The content background a block ultimately sits on when it sets none of its
// own — the wrapper color, else the global content color.
function contentBg(gs: GlobalStyles, wrapper: WrapperSettings): string {
  return wrapper.bgColor || gs.contentBgColor || '#ffffff';
}

// ---- Props ----------------------------------------------------------------

/** A node the inline text editor should be opened on. */
export interface InlineEditRequest {
  layerId: string;
  columnId?: string;
  elementId?: string;
}

interface DndCanvasProps {
  layers: Layer[];
  globalStyles: GlobalStyles;
  wrapper: WrapperSettings;
  selection: SelectionState;
  device: 'desktop' | 'mobile';
  onSelectLayer: (id: string | null) => void;
  onSelectElement: (layerId: string, columnId: string, elementId: string) => void;
  onSelectColumn: (layerId: string, columnId: string) => void;
  onRemoveLayer: (id: string) => void;
  onDuplicateLayer: (id: string) => void;
  onUpdateLayer: (id: string, patch: Partial<Layer>) => void;
  onUpdateElement: (layerId: string, columnId: string, elementId: string, patch: Partial<DndElement>) => void;
  onRemoveElement: (layerId: string, columnId: string, elementId: string) => void;
  onDuplicateElement: (layerId: string, columnId: string, elementId: string) => void;
  onAddBlockToColumn: (layerId: string, columnId: string, type: ElementType) => void;
  onOpenPhotoPicker: (cb: (url: string, alt: string) => void) => void;
  onOpenLinkModal: (hasSelection: boolean, cb: (url: string, text: string) => void) => void;
  onOpenVariableModal: (cb: (token: string) => void) => void;
  onOpenHtmlModal: (initialHtml: string, cb: (html: string) => void) => void;
  onOpenSocialModal: (links: SocialLink[], logoSetId: number | null, previewBg: string, cb: (result: SocialModalResult) => void) => void;
  onOpenColumnsModal: (currentLayout: ColumnsLayout, cb: (layout: ColumnsLayout) => void) => void;
  /**
   * Ask the canvas to open the inline text editor on a node. Inline editing is
   * canvas-local state, so the inspector (a sibling panel) requests it through
   * here rather than reaching into the canvas.
   */
  editRequest: InlineEditRequest | null;
  onEditRequestHandled: () => void;
  // Palette block drag
  isDragging: boolean;
  draggedBlockTier: BlockTier | null;
  dropTargetIndex: number | null;
  onDropIndexChange: (index: number | null) => void;
  columnDropTarget: { layerId: string; columnId: string } | null;
  onColumnDropTargetChange: (target: { layerId: string; columnId: string } | null) => void;
  onCanvasDrop: () => void;
  // Layer reorder drag
  isReordering: boolean;
  reorderFromIndex: number | null;
  onReorderStart: (fromIndex: number) => void;
  onReorderEnd: () => void;
  onReorderDrop: (toIndex: number) => void;
  // Element move drag (dragging a placed block within/between columns)
  isElementDragging: boolean;
  elementDropTarget: { layerId: string; columnId: string; index: number } | null;
  onElementDragStart: (layerId: string, columnId: string, index: number) => void;
  onElementDropTargetChange: (target: { layerId: string; columnId: string; index: number } | null) => void;
  onElementDrop: () => void;
  onElementDragOut: (toIndex: number) => void;
  onElementDragEnd: () => void;
}

// ---- Element Renderers ----------------------------------------------------

function RenderTextElement({ el, gs }: { el: TextElement; gs: GlobalStyles }) {
  const style: CSSProperties = {
    padding: spacingToCss(el.padding),
    fontFamily: gs.fontFamily,
    fontSize: gs.fontSize,
    color: gs.textColor,
    lineHeight: el.lineHeight || gs.lineHeight,
    textAlign: el.textAlign,
    wordBreak: 'break-word',
  };
  // Inline the global heading/paragraph/link styles so the canvas matches the
  // compiled export (which also inlines them) and isn't flattened by Tailwind
  // Preflight. Same shared helper the compiler uses — single source of truth.
  // stripPageAffectingTags keeps a pasted <style> block from leaking out of the
  // canvas and restyling the app (canvas is not an iframe).
  const html = useMemo(() => inlineContentStyles(stripPageAffectingTags(el.content), gs), [el.content, gs]);
  if (!el.content || el.content === '<p></p>' || el.content.trim() === '') {
    return <div style={style} className="dnd-placeholder">Double-click to add text</div>;
  }
  return <div style={style} dangerouslySetInnerHTML={{ __html: html }} />;
}

function imageAlignMargin(align: string): string | undefined {
  if (align === 'center') return '0 auto';
  if (align === 'right') return '0 0 0 auto';
  return undefined;
}

function RenderImageElement({ el, colPxWidth }: { el: ImageElement; colPxWidth: number }) {
  // Auto-width images fill the column exactly (max-width:100%) so no hairline
  // seam shows between cells; fixed-width images cap at their px width.
  const maxW: number | string = el.autoWidth ? '100%' : Math.min(el.width, colPxWidth);
  const style: CSSProperties = { padding: spacingToCss(el.padding), textAlign: el.align };
  if (!el.src) {
    return (
      <div style={style}>
        <div className="dnd-image-placeholder" style={{ maxWidth: maxW, borderRadius: el.borderRadius || 0, margin: imageAlignMargin(el.align) }}>
          <ImageIcon size={28} strokeWidth={1.5} aria-hidden />
          <span>Double-click to add image</span>
        </div>
      </div>
    );
  }
  return (
    <div style={style}>
      <img src={el.src} alt={el.alt || 'Image'} style={{ display: 'block', maxWidth: maxW, width: '100%', height: 'auto', borderRadius: el.borderRadius || 0, margin: imageAlignMargin(el.align) }} />
    </div>
  );
}

function RenderButtonElement({ el, gs }: { el: ButtonElement; gs: GlobalStyles }) {
  const style: CSSProperties = { padding: spacingToCss(el.padding), textAlign: el.align };
  const btnStyle: CSSProperties = {
    display: 'inline-block',
    backgroundColor: el.bgColor || gs.defaultButtonStyle.bgColor,
    color: el.textColor || gs.defaultButtonStyle.textColor,
    fontFamily: el.fontFamily,
    fontSize: el.fontSize,
    fontWeight: el.fontWeight,
    borderRadius: el.borderRadius,
    padding: spacingToCss(el.buttonPadding),
    textDecoration: 'none',
    textAlign: 'center',
    border: 'none',
    cursor: 'default',
    width: el.fullWidth ? '100%' : undefined,
  };
  return <div style={style}><span style={btnStyle}>{el.text || 'Click Here'}</span></div>;
}

function RenderDividerElement({ el }: { el: DividerElement }) {
  return (
    <div style={{ padding: spacingToCss(el.padding) }}>
      <hr style={{ border: 0, borderTop: `${el.borderWidth}px ${el.borderStyle} ${el.borderColor}`, width: el.dividerWidth, margin: '0 auto' }} />
    </div>
  );
}

function RenderSpacerElement({ el }: { el: SpacerElement }) {
  return <div style={{ height: el.height, fontSize: 1, lineHeight: `${el.height}px` }}>&nbsp;</div>;
}

function RenderHtmlElement({ el }: { el: HtmlElement }) {
  // The block keeps its <style> in the model and in the sent email; it is only
  // withheld from the canvas, which shares the app's document.
  const html = useMemo(() => stripPageAffectingTags(el.content), [el.content]);
  if (!el.content?.trim()) {
    return <div style={{ padding: spacingToCss(el.padding) }} className="dnd-placeholder">Double-click to edit HTML</div>;
  }
  return <div style={{ padding: spacingToCss(el.padding) }} dangerouslySetInnerHTML={{ __html: html }} />;
}

function RenderSocialElement({ el }: { el: SocialElement }) {
  const style: CSSProperties = { padding: spacingToCss(el.padding), textAlign: el.align };
  if (el.iconColor) style.color = el.iconColor;
  if (el.lineHeight) style.lineHeight = el.lineHeight;
  const enabled = el.links.filter((l) => l.enabled);
  if (!enabled.length) return <div style={style} className="dnd-placeholder">Enable social platforms in the inspector</div>;
  return (
    <div style={style}>
      {enabled.map((link) => {
        const logoUrl = el.logos?.[link.platform];
        const label = link.platform.charAt(0).toUpperCase() + link.platform.slice(1);
        return (
        <span key={link.platform} title={label} style={{ display: 'inline-block', padding: `0 ${el.spacing}px`, verticalAlign: 'middle' }}>
          {el.style !== 'text-only' && (logoUrl ? (
            <img
              src={logoUrl}
              alt={label}
              title={label}
              width={el.iconSize}
              height={el.iconSize}
              style={{ display: 'inline-block', verticalAlign: 'middle', border: 0 }}
            />
          ) : SOCIAL_SVG_PATHS[link.platform] && (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width={el.iconSize}
              height={el.iconSize}
              viewBox="0 0 24 24"
              fill="currentColor"
              style={{ display: 'inline-block', verticalAlign: 'middle' }}
            >
              <path d={SOCIAL_SVG_PATHS[link.platform]} />
            </svg>
          ))}
          {el.style !== 'icons-only' && (
            <span style={{ verticalAlign: 'middle', marginLeft: 4, textTransform: 'capitalize' }}>{link.platform}</span>
          )}
        </span>
        );
      })}
    </div>
  );
}

function RenderElement({ el, gs, colPxWidth }: { el: DndElement; gs: GlobalStyles; colPxWidth: number }) {
  // Mirror of the compiler's box-wrap (see compileElement): with a visible
  // border the element's padding moves onto the bordered box and the core
  // renders unpadded; margin becomes padding on one more transparent wrapper.
  const bordered = hasVisibleBorder(el.border);
  const margined = hasSpacing(el.margin);
  const coreEl = bordered
    ? ({ ...el, padding: { top: 0, right: 0, bottom: 0, left: 0 } } as DndElement)
    : el;
  let node = <RenderElementCore el={coreEl} gs={gs} colPxWidth={colPxWidth} />;
  if (bordered) {
    node = <div style={{ ...bordersToStyle(el.border), padding: spacingToCss(el.padding) }}>{node}</div>;
  }
  if (margined) {
    node = <div style={{ padding: spacingToCss(el.margin) }}>{node}</div>;
  }
  return node;
}

function RenderElementCore({ el, gs, colPxWidth }: { el: DndElement; gs: GlobalStyles; colPxWidth: number }) {
  switch (el.type) {
    case 'text': return <RenderTextElement el={el} gs={gs} />;
    case 'image': return <RenderImageElement el={el} colPxWidth={colPxWidth} />;
    case 'button': return <RenderButtonElement el={el} gs={gs} />;
    case 'divider': return <RenderDividerElement el={el} />;
    case 'spacer': return <RenderSpacerElement el={el} />;
    case 'html': return <RenderHtmlElement el={el} />;
    case 'social': return <RenderSocialElement el={el} />;
  }
}

// ---- Layer Renderers ------------------------------------------------------

function RenderTextLayer({ layer, gs }: { layer: TextLayer; gs: GlobalStyles }) {
  const style: CSSProperties = {
    ...layerBaseStyle(layer),
    fontFamily: gs.fontFamily,
    fontSize: gs.fontSize,
    color: layer.textColor || gs.textColor,
    lineHeight: layer.lineHeight || gs.lineHeight,
    textAlign: layer.textAlign,
    wordBreak: 'break-word',
  };
  // See RenderTextElement: inline typography so canvas == compiled export.
  const html = useMemo(() => inlineContentStyles(stripPageAffectingTags(layer.content), gs), [layer.content, gs]);
  if (!layer.content || layer.content === '<p></p>' || layer.content.trim() === '') {
    return <div style={style} className="dnd-placeholder">Double-click to add text</div>;
  }
  return <div style={style} dangerouslySetInnerHTML={{ __html: html }} />;
}

function RenderImageLayer({ layer, maxWidth }: { layer: ImageLayer; maxWidth: number }) {
  const w = layer.autoWidth ? maxWidth : Math.min(layer.width, maxWidth);
  const style: CSSProperties = {
    ...layerBaseStyle(layer),
    textAlign: layer.align,
  };
  if (!layer.src) {
    return (
      <div style={style}>
        <div className="dnd-image-placeholder" style={{ maxWidth: w, borderRadius: layer.borderRadius || 0, margin: imageAlignMargin(layer.align) }}>
          <ImageIcon size={28} strokeWidth={1.5} aria-hidden />
          <span>Double-click to add image</span>
        </div>
      </div>
    );
  }
  return (
    <div style={style}>
      <img src={layer.src} alt={layer.alt || 'Image'} style={{ display: 'block', maxWidth: w, width: '100%', height: 'auto', borderRadius: layer.borderRadius || 0, margin: imageAlignMargin(layer.align) }} />
    </div>
  );
}

function RenderButtonLayer({ layer, gs }: { layer: ButtonLayer; gs: GlobalStyles }) {
  const style: CSSProperties = {
    ...layerBaseStyle(layer),
    textAlign: layer.align,
  };
  const btnStyle: CSSProperties = {
    display: 'inline-block',
    backgroundColor: layer.buttonBgColor || gs.defaultButtonStyle.bgColor,
    color: layer.textColor || gs.defaultButtonStyle.textColor,
    fontFamily: layer.fontFamily,
    fontSize: layer.fontSize,
    fontWeight: layer.fontWeight,
    borderRadius: layer.borderRadius,
    padding: spacingToCss(layer.buttonPadding),
    textDecoration: 'none',
    textAlign: 'center',
    border: 'none',
    cursor: 'default',
    width: layer.fullWidth ? '100%' : undefined,
  };
  return <div style={style}><span style={btnStyle}>{layer.text || 'Click Here'}</span></div>;
}

function RenderDividerLayer({ layer }: { layer: DividerLayer }) {
  const style: CSSProperties = { ...layerBaseStyle(layer), textAlign: layer.align };
  const hrStyle: CSSProperties = {
    border: 0,
    borderTop: `${layer.borderWidth}px ${layer.borderStyle} ${layer.borderColor}`,
    width: layer.dividerWidth,
    margin: layer.align === 'left' ? '0' : layer.align === 'right' ? '0 0 0 auto' : '0 auto',
  };
  return <div style={style}><hr style={hrStyle} /></div>;
}

function RenderSpacerLayer({ layer }: { layer: SpacerLayer }) {
  return <div style={{ height: layer.height, fontSize: 1, lineHeight: `${layer.height}px`, backgroundColor: layer.bgColor || undefined }}>&nbsp;</div>;
}

function RenderHtmlLayer({ layer }: { layer: HtmlLayer }) {
  const style: CSSProperties = layerBaseStyle(layer);
  // See RenderHtmlElement: withheld from the canvas only, not from the email.
  const html = useMemo(() => stripPageAffectingTags(layer.content), [layer.content]);
  if (!layer.content?.trim()) {
    return <div style={style} className="dnd-placeholder">Double-click to edit HTML</div>;
  }
  return <div style={style} dangerouslySetInnerHTML={{ __html: html }} />;
}

function RenderSocialLayer({ layer }: { layer: SocialLayer }) {
  const style: CSSProperties = { ...layerBaseStyle(layer), textAlign: layer.align };
  if (layer.iconColor) style.color = layer.iconColor;
  if (layer.lineHeight) style.lineHeight = layer.lineHeight;
  const enabled = layer.links.filter((l) => l.enabled);
  if (!enabled.length) return <div style={style} className="dnd-placeholder">Enable social platforms in the inspector</div>;
  return (
    <div style={style}>
      {enabled.map((link) => {
        const logoUrl = layer.logos?.[link.platform];
        const label = link.platform.charAt(0).toUpperCase() + link.platform.slice(1);
        return (
        <span key={link.platform} title={label} style={{ display: 'inline-block', padding: `0 ${layer.spacing}px`, verticalAlign: 'middle' }}>
          {layer.style !== 'text-only' && (logoUrl ? (
            <img
              src={logoUrl}
              alt={label}
              title={label}
              width={layer.iconSize}
              height={layer.iconSize}
              style={{ display: 'inline-block', verticalAlign: 'middle', border: 0 }}
            />
          ) : SOCIAL_SVG_PATHS[link.platform] && (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width={layer.iconSize}
              height={layer.iconSize}
              viewBox="0 0 24 24"
              fill="currentColor"
              style={{ display: 'inline-block', verticalAlign: 'middle' }}
            >
              <path d={SOCIAL_SVG_PATHS[link.platform]} />
            </svg>
          ))}
          {layer.style !== 'icons-only' && (
            <span style={{ verticalAlign: 'middle', marginLeft: 4, textTransform: 'capitalize' }}>{link.platform}</span>
          )}
        </span>
        );
      })}
    </div>
  );
}

function RenderVideoLayer({ layer, maxWidth }: { layer: VideoLayer; maxWidth: number }) {
  const style: CSSProperties = { ...layerBaseStyle(layer), textAlign: 'center' as const };
  if (!layer.thumbnailSrc) {
    return (
      <div style={style}>
        <div className="dnd-image-placeholder" style={{ maxWidth, margin: '0 auto' }}>
          <Video size={28} strokeWidth={1.5} aria-hidden />
          <span>Double-click to add video thumbnail</span>
        </div>
      </div>
    );
  }
  return (
    <div style={style}>
      <img src={layer.thumbnailSrc} alt={layer.alt || 'Video'} style={{ display: 'block', maxWidth, width: '100%', height: 'auto', margin: '0 auto' }} />
    </div>
  );
}

function RenderCountdownLayer({ layer }: { layer: CountdownLayer }) {
  const style: CSSProperties = { ...layerBaseStyle(layer), textAlign: 'center' as const };
  return (
    <div style={style}>
      <div style={{ display: 'flex', justifyContent: 'center', gap: 16, padding: '16px 0' }}>
        {(['Days', 'Hours', 'Minutes', 'Seconds'] as const).map((label) => (
          <div key={label} style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 28, fontWeight: 'bold', color: layer.digitColor, backgroundColor: layer.digitBgColor, borderRadius: 6, padding: '8px 12px', minWidth: 48 }}>00</div>
            {layer.showLabels && <div style={{ fontSize: 11, color: layer.textColor || layer.labelColor, marginTop: 4 }}>{layer.labels[label.toLowerCase() as keyof typeof layer.labels]}</div>}
          </div>
        ))}
      </div>
    </div>
  );
}

// ---- Column + Elements Wrapper --------------------------------------------

function ColumnCell({
  col,
  colWidthPct,
  colPxWidth,
  gapRight,
  layerId,
  gs,
  selection,
  hoveredElementId,
  editingElementId,
  editOpenPoint,
  onHoverElement,
  onClickElement,
  onDblClickElement,
  onElementToolbarAction,
  onSelectColumn,
  onUpdateElement,
  onExitEditing,
  onOpenLinkModal,
  onOpenVariableModal,
  isDragging,
  isReordering,
  draggedBlockTier,
  columnDropTarget,
  onColumnDragOver,
  onColumnDragLeave,
  isElementDragging,
  elementDropTarget,
  onElementDragStart,
  onElementDragEnd,
  onElementDropTargetChange,
  onElementDrop,
  onAddBlockToColumn,
}: {
  col: Column;
  colWidthPct: number;
  colPxWidth: number;
  gapRight: number;
  layerId: string;
  gs: GlobalStyles;
  selection: SelectionState;
  hoveredElementId: string | null;
  editingElementId: string | null;
  editOpenPoint: { x: number; y: number } | null;
  onHoverElement: (id: string | null) => void;
  onClickElement: (layerId: string, columnId: string, elementId: string) => void;
  onDblClickElement: (el: DndElement, layerId: string, columnId: string, point: { x: number; y: number } | null) => void;
  onElementToolbarAction: (action: string, layerId: string, columnId: string, elementId: string) => void;
  onSelectColumn: (layerId: string, columnId: string) => void;
  onUpdateElement: (layerId: string, columnId: string, elementId: string, patch: Partial<DndElement>) => void;
  onExitEditing: () => void;
  onOpenLinkModal: (hasSelection: boolean, cb: (url: string, text: string) => void) => void;
  onOpenVariableModal: (cb: (token: string) => void) => void;
  isDragging: boolean;
  isReordering: boolean;
  draggedBlockTier: BlockTier | null;
  columnDropTarget: { layerId: string; columnId: string } | null;
  onColumnDragOver: (e: React.DragEvent, layerId: string, columnId: string) => void;
  onColumnDragLeave: () => void;
  isElementDragging: boolean;
  elementDropTarget: { layerId: string; columnId: string; index: number } | null;
  onElementDragStart: (layerId: string, columnId: string, index: number) => void;
  onElementDragEnd: () => void;
  onElementDropTargetChange: (target: { layerId: string; columnId: string; index: number } | null) => void;
  onElementDrop: () => void;
  onAddBlockToColumn: (layerId: string, columnId: string, type: ElementType) => void;
}) {
  const canBeElement = draggedBlockTier === 'element' || draggedBlockTier === 'both';
  const acceptsDrop = (isDragging || isReordering) && canBeElement;
  const isDropTarget = columnDropTarget?.layerId === layerId && columnDropTarget?.columnId === col.id;
  const isColumnSelected = selection.columnId === col.id && !selection.elementId;
  const tdRef = useRef<HTMLTableCellElement>(null);
  const [addMenuOpen, setAddMenuOpen] = useState(false);
  const [addMenuUp, setAddMenuUp] = useState(false);
  const addWrapRef = useRef<HTMLDivElement>(null);
  const addBtnRef = useRef<HTMLButtonElement>(null);
  const addMenuRef = useRef<HTMLDivElement>(null);

  // Close this cell's add-block menu on any click outside its own wrapper
  // (including another column's add-block button).
  useEffect(() => {
    if (!addMenuOpen) return;
    const handler = (e: MouseEvent) => {
      if (!addWrapRef.current?.contains(e.target as Node)) setAddMenuOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [addMenuOpen]);

  // Flip the menu above the button when there isn't room below the viewport.
  // Runs before paint so there's no visible jump.
  useLayoutEffect(() => {
    if (!addMenuOpen) {
      setAddMenuUp(false);
      return;
    }
    const btn = addBtnRef.current;
    const menu = addMenuRef.current;
    if (!btn || !menu) return;
    const btnRect = btn.getBoundingClientRect();
    const menuH = menu.offsetHeight;
    const spaceBelow = window.innerHeight - btnRect.bottom;
    const spaceAbove = btnRect.top;
    setAddMenuUp(spaceBelow < menuH + 12 && spaceAbove > spaceBelow);
  }, [addMenuOpen]);

  const dropIdxHere =
    isElementDragging && elementDropTarget?.layerId === layerId && elementDropTarget?.columnId === col.id
      ? elementDropTarget.index
      : -1;

  // Which slot (0..elements.length) the pointer is over, by element midpoints.
  const computeElementDropIndex = (e: React.DragEvent): number => {
    const cell = tdRef.current;
    if (!cell) return col.elements.length;
    const els = cell.querySelectorAll('[data-el-idx]');
    let best = 0;
    els.forEach((node) => {
      const rect = node.getBoundingClientRect();
      if (e.clientY > rect.top + rect.height / 2) best = Number(node.getAttribute('data-el-idx')) + 1;
    });
    return best;
  };

  const cellStyle: CSSProperties = {
    width: `${colWidthPct}%`,
    padding: spacingToCss(col.padding),
    // Column gap: extra space on the right of every column except the last,
    // mirroring the compiler's padding-right approach so preview == export.
    ...(gapRight > 0 ? { paddingRight: gapRight } : {}),
    backgroundColor: col.bgColor || undefined,
    verticalAlign: col.valign,
    ...bordersToStyle(col.border),
    minHeight: 40,
    position: 'relative',
  };

  const dropBar = <div className="dnd-element-drop-bar" />;

  return (
    <td
      ref={tdRef}
      style={cellStyle}
      className={`dnd-col-cell${isDropTarget ? ' drop-target' : ''}${isColumnSelected ? ' selected' : ''}`}
      onClick={(e) => { e.stopPropagation(); onSelectColumn(layerId, col.id); }}
      onDragOver={(e) => {
        if (isElementDragging) {
          e.preventDefault();
          e.stopPropagation();
          onElementDropTargetChange({ layerId, columnId: col.id, index: computeElementDropIndex(e) });
        } else if (acceptsDrop) {
          e.preventDefault();
          e.stopPropagation();
          onColumnDragOver(e, layerId, col.id);
        }
      }}
      onDragLeave={onColumnDragLeave}
      onDrop={(e) => {
        e.preventDefault();
        if (isElementDragging) { e.stopPropagation(); onElementDrop(); }
      }}
    >
      {col.elements.length === 0 && (
        <div className="dnd-col-empty">
          {dropIdxHere === 0 ? dropBar : (
            <div className="dnd-add-block-wrap" ref={addWrapRef}>
              <button
                ref={addBtnRef}
                type="button"
                className="dnd-col-add-block"
                onClick={(e) => { e.stopPropagation(); setAddMenuOpen((o) => !o); }}
                title="Add a block to this column"
              >
                <Plus size={14} /> Add block
              </button>
              {addMenuOpen && (
                <div
                  ref={addMenuRef}
                  className={`dnd-add-block-menu${addMenuUp ? ' dnd-add-block-menu--up' : ''}`}
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="dnd-add-block-menu-title">Add a block</div>
                  {ELEMENT_BLOCKS.map((b) => {
                    const Icon = b.icon;
                    return (
                      <button
                        key={b.id}
                        type="button"
                        className="dnd-add-block-item"
                        onClick={(e) => {
                          e.stopPropagation();
                          onAddBlockToColumn(layerId, col.id, b.type as ElementType);
                          setAddMenuOpen(false);
                        }}
                      >
                        <span className="dnd-add-block-icon"><Icon size={15} /></span>
                        <span className="dnd-add-block-label">{b.label}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      )}
      {col.elements.map((el, elIdx) => {
        const isHovered = hoveredElementId === el.id && selection.elementId !== el.id;
        const isSelected = selection.elementId === el.id;
        const isEditing = editingElementId === el.id;
        return (
          <div
            key={el.id}
            data-el-idx={elIdx}
            className={`dnd-element-render${isHovered ? ' hovered' : ''}${isSelected ? ' selected' : ''}`}
            onMouseEnter={() => onHoverElement(el.id)}
            onMouseLeave={() => onHoverElement(null)}
            onClick={(e) => { e.stopPropagation(); onClickElement(layerId, col.id, el.id); }}
            onDoubleClick={(e) => {
              e.stopPropagation();
              onDblClickElement(el, layerId, col.id, { x: e.clientX, y: e.clientY });
            }}
          >
            {dropIdxHere === elIdx && dropBar}
            {isSelected && !isEditing && (
              <div className="dnd-element-toolbar" onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}>
                <span
                  className="dnd-element-drag-handle"
                  draggable
                  onDragStart={(e) => {
                    e.stopPropagation();
                    e.dataTransfer.effectAllowed = 'move';
                    e.dataTransfer.setData('text/plain', `element-${el.id}`);
                    onElementDragStart(layerId, col.id, elIdx);
                  }}
                  onDragEnd={onElementDragEnd}
                  onMouseDown={(e) => e.stopPropagation()}
                  title="Drag to move"
                >
                  <GripVertical size={12} />
                </span>
                {(el.type === 'text' || el.type === 'image') && (
                  <button
                    title={(el.locked ?? true) ? 'Locked for editors — click to unlock' : 'Unlocked for editors — click to lock'}
                    className={(el.locked ?? true) ? undefined : 'dnd-toolbar-unlocked'}
                    onClick={(e) => { e.stopPropagation(); onUpdateElement(layerId, col.id, el.id, { locked: !(el.locked ?? true) } as Partial<DndElement>); }}
                  >
                    {(el.locked ?? true) ? <Lock size={12} /> : <LockOpen size={12} />}
                  </button>
                )}
                {el.type === 'text' && (
                  <button
                    title="Edit text"
                    onClick={(e) => { e.stopPropagation(); onDblClickElement(el, layerId, col.id, null); }}
                  >
                    <Pencil size={12} />
                  </button>
                )}
                {el.type === 'image' && (
                  <button
                    title="Change image"
                    onClick={(e) => { e.stopPropagation(); onDblClickElement(el, layerId, col.id, null); }}
                  >
                    <ImageIcon size={12} />
                  </button>
                )}
                {el.type === 'social' && (
                  <button
                    title="Edit social links"
                    onClick={(e) => { e.stopPropagation(); onDblClickElement(el, layerId, col.id, null); }}
                  >
                    <Pencil size={12} />
                  </button>
                )}
                <button title="Duplicate" onClick={(e) => { e.stopPropagation(); onElementToolbarAction('duplicate', layerId, col.id, el.id); }}>
                  <Copy size={12} />
                </button>
                <button title="Delete" className="dnd-action-delete-btn" onClick={(e) => { e.stopPropagation(); onElementToolbarAction('delete', layerId, col.id, el.id); }}>
                  <Trash2 size={12} />
                </button>
              </div>
            )}
            {isEditing && el.type === 'text' ? (
              // The outer-spacing wrapper mirrors RenderElement so the text
              // doesn't jump when editing starts (padding, not CSS margin,
              // which could collapse out of the wrapper).
              <div style={hasSpacing(el.margin) ? { padding: spacingToCss(el.margin) } : undefined}>
                <InlineTextEditor
                  initialHtml={(el as TextElement).content}
                  nodeType="text"
                  align={(el as TextElement).textAlign}
                  editableStyle={{
                    // Keep the element's border while editing too; padding
                    // sits inside it, matching RenderElement/the compiler.
                    ...(el.border ? bordersToStyle(el.border) : {}),
                    padding: spacingToCss(el.padding),
                    fontFamily: gs.fontFamily,
                    fontSize: gs.fontSize,
                    color: gs.textColor,
                    lineHeight: gs.lineHeight,
                    textAlign: el.type === 'text' ? (el as TextElement).textAlign : undefined,
                    wordBreak: 'break-word',
                    outline: 'none',
                    minHeight: '1em',
                  }}
                  openAtPoint={editOpenPoint}
                  linkColor={gs.linkColor}
                  onInput={(html) => onUpdateElement(layerId, col.id, el.id, { content: html } as Partial<DndElement>)}
                  onAlignChange={(a) => onUpdateElement(layerId, col.id, el.id, { textAlign: a } as Partial<DndElement>)}
                  onExit={onExitEditing}
                  onOpenLinkModal={onOpenLinkModal}
                  onOpenVariableModal={onOpenVariableModal}
                />
              </div>
            ) : (
              <RenderElement el={el} gs={gs} colPxWidth={colPxWidth} />
            )}
          </div>
        );
      })}
      {col.elements.length > 0 && dropIdxHere === col.elements.length && dropBar}
    </td>
  );
}

// ---- Main Canvas Component ------------------------------------------------

export function DndCanvas({
  layers,
  globalStyles,
  wrapper,
  selection,
  device,
  onSelectLayer,
  onSelectElement,
  onSelectColumn,
  onRemoveLayer,
  onDuplicateLayer,
  onUpdateLayer,
  onUpdateElement,
  onRemoveElement,
  onDuplicateElement,
  onAddBlockToColumn,
  onOpenPhotoPicker,
  onOpenLinkModal,
  onOpenVariableModal,
  onOpenHtmlModal,
  onOpenSocialModal,
  onOpenColumnsModal,
  editRequest,
  onEditRequestHandled,
  isDragging,
  draggedBlockTier,
  dropTargetIndex,
  onDropIndexChange,
  columnDropTarget,
  onColumnDropTargetChange,
  onCanvasDrop,
  isReordering,
  reorderFromIndex,
  onReorderStart,
  onReorderEnd,
  onReorderDrop,
  isElementDragging,
  elementDropTarget,
  onElementDragStart,
  onElementDropTargetChange,
  onElementDrop,
  onElementDragOut,
  onElementDragEnd,
}: DndCanvasProps) {
  const [hoveredLayerId, setHoveredLayerId] = useState<string | null>(null);
  const [hoveredElementId, setHoveredElementId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  // The double-click coordinates, used by InlineTextEditor to select the clicked word.
  const [editOpenPoint, setEditOpenPoint] = useState<{ x: number; y: number } | null>(null);
  // Whether the most-recent mousedown started inside the inline text editor.
  // Used to ignore the spurious click fired when a text drag-select is released
  // outside the editable (on the canvas), which would otherwise end editing.
  const mouseDownInEditorRef = useRef(false);
  const canvasRef = useRef<HTMLDivElement>(null);
  const layerRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  const maxWidth = wrapper.maxWidth;

  // ---- Exit inline editing ----

  // Content is committed live by InlineTextEditor (onInput + unmount flush),
  // so exiting is just clearing the editing state.
  const exitEditing = useCallback(() => {
    setEditingId(null);
    setEditOpenPoint(null);
  }, []);

  // ---- Inline-edit requests from outside the canvas (inspector button) ----

  // The element id when editing a block inside a column, else the layer id —
  // the same identity the double-click path sets.
  useEffect(() => {
    if (!editRequest) return;
    setEditingId(editRequest.elementId ?? editRequest.layerId);
    setEditOpenPoint(null); // no click coordinates, so no word is preselected
    onEditRequestHandled();
  }, [editRequest, onEditRequestHandled]);

  // ---- Handle layer click ----

  const handleLayerClick = useCallback((e: React.MouseEvent, layerId: string) => {
    e.stopPropagation();
    if (editingId && mouseDownInEditorRef.current) return;
    if (editingId) exitEditing();
    onSelectLayer(layerId);
  }, [editingId, exitEditing, onSelectLayer]);

  // ---- Handle element click ----

  const handleElementClick = useCallback((layerId: string, columnId: string, elementId: string) => {
    if (editingId && mouseDownInEditorRef.current) return;
    if (editingId && editingId !== elementId) exitEditing();
    onSelectElement(layerId, columnId, elementId);
  }, [editingId, exitEditing, onSelectElement]);

  // ---- Handle column click ----

  const handleColumnClick = useCallback((layerId: string, columnId: string) => {
    if (editingId && mouseDownInEditorRef.current) return;
    if (editingId) exitEditing();
    onSelectColumn(layerId, columnId);
  }, [editingId, exitEditing, onSelectColumn]);

  // ---- Handle double-click for editing ----

  const handleDblClickLayer = useCallback((layer: Layer, point: { x: number; y: number } | null) => {
    if (layer.type === 'text') {
      setEditOpenPoint(point);
      setEditingId(layer.id);
    } else if (layer.type === 'html') {
      onOpenHtmlModal((layer as HtmlLayer).content, (html) => {
        onUpdateLayer(layer.id, { content: html } as Partial<Layer>);
      });
    } else if (layer.type === 'image') {
      onOpenPhotoPicker((url, alt) => {
        onUpdateLayer(layer.id, { src: url, alt: alt || (layer as ImageLayer).alt } as Partial<Layer>);
      });
    } else if (layer.type === 'video') {
      onOpenPhotoPicker((url, alt) => {
        onUpdateLayer(layer.id, { thumbnailSrc: url, alt: alt || (layer as VideoLayer).alt } as Partial<Layer>);
      });
    } else if (layer.type === 'social') {
      const social = layer as SocialLayer;
      const previewBg = social.bgColor || contentBg(globalStyles, wrapper);
      onOpenSocialModal(social.links, social.logoSetId ?? null, previewBg, ({ links, logoSetId, logos }) => {
        onUpdateLayer(layer.id, { links, logoSetId, logos } as Partial<Layer>);
      });
    }
  }, [onOpenPhotoPicker, onOpenHtmlModal, onOpenSocialModal, onUpdateLayer, globalStyles, wrapper]);

  // Toolbar "Edit" shortcut for layers whose main content lives in a modal:
  // columns (layout picker) and social (links). Mirrors the modals opened on add.
  const handleEditLayer = useCallback((layer: Layer) => {
    if (layer.type === 'social') {
      const social = layer as SocialLayer;
      const previewBg = social.bgColor || contentBg(globalStyles, wrapper);
      onOpenSocialModal(social.links, social.logoSetId ?? null, previewBg, ({ links, logoSetId, logos }) => {
        onUpdateLayer(layer.id, { links, logoSetId, logos } as Partial<Layer>);
      });
    } else if (layer.type === 'columns') {
      onOpenColumnsModal((layer as ColumnsLayer).layout, (layout) => {
        onUpdateLayer(layer.id, changeColumnsLayout(layer as ColumnsLayer, layout));
      });
    }
  }, [onOpenSocialModal, onOpenColumnsModal, onUpdateLayer, globalStyles, wrapper]);

  const layerHasEditModal = (type: Layer['type']) => type === 'social' || type === 'columns';

  const handleDblClickElement = useCallback((el: DndElement, layerId: string, columnId: string, point: { x: number; y: number } | null) => {
    if (el.type === 'text') {
      setEditOpenPoint(point);
      setEditingId(el.id);
    } else if (el.type === 'html') {
      onOpenHtmlModal((el as HtmlElement).content, (html) => {
        onUpdateElement(layerId, columnId, el.id, { content: html } as Partial<DndElement>);
      });
    } else if (el.type === 'image') {
      onOpenPhotoPicker((url, alt) => {
        onUpdateElement(layerId, columnId, el.id, { src: url, alt: alt || (el as ImageElement).alt } as Partial<DndElement>);
      });
    } else if (el.type === 'social') {
      const social = el as SocialElement;
      const parent = layers.find((l) => l.id === layerId);
      const col = parent && parent.type === 'columns' ? parent.columns.find((c) => c.id === columnId) : undefined;
      const previewBg = col?.bgColor || parent?.bgColor || contentBg(globalStyles, wrapper);
      onOpenSocialModal(social.links, social.logoSetId ?? null, previewBg, ({ links, logoSetId, logos }) => {
        onUpdateElement(layerId, columnId, el.id, { links, logoSetId, logos } as Partial<DndElement>);
      });
    }
  }, [onOpenPhotoPicker, onOpenHtmlModal, onOpenSocialModal, onUpdateElement, layers, globalStyles, wrapper]);

  // ---- Layer toolbar actions ----

  const handleLayerToolbarAction = useCallback((action: string, layerId: string, _layerIndex: number) => {
    switch (action) {
      case 'duplicate': onDuplicateLayer(layerId); break;
      case 'delete': onRemoveLayer(layerId); break;
    }
  }, [onDuplicateLayer, onRemoveLayer]);

  // ---- Element toolbar actions ----

  const handleElementToolbarAction = useCallback((action: string, layerId: string, columnId: string, elementId: string) => {
    switch (action) {
      case 'delete': onRemoveElement(layerId, columnId, elementId); break;
      case 'duplicate': onDuplicateElement(layerId, columnId, elementId); break;
    }
  }, [onRemoveElement, onDuplicateElement]);

  // ---- Canvas background click ----

  const handleCanvasClick = useCallback(() => {
    if (editingId && mouseDownInEditorRef.current) return;
    if (editingId) exitEditing();
    onSelectLayer(null);
  }, [editingId, exitEditing, onSelectLayer]);

  // ---- Click outside to exit editing ----
  // (Enter/Escape are handled inside InlineTextEditor while the editable is focused.)

  useEffect(() => {
    if (!editingId) return;
    // Capture phase: record whether each gesture began inside the editor, before
    // the wrapper's stopPropagation can hide it. The click handlers use this to
    // ignore the spurious click when a drag-select is released on the canvas.
    const onDown = (e: MouseEvent) => {
      mouseDownInEditorRef.current = !!(e.target as Element).closest('.dnd-inline-editor');
    };
    const handler = (e: MouseEvent) => {
      const target = e.target as Element;
      // Clicks inside the inline editor (editable or its toolbar) or a modal
      // (link/variable pickers are portaled to body) must not end the session.
      if (target.closest('.dnd-inline-editor')) return;
      if (target.closest('[data-state], [role="dialog"]')) return;
      exitEditing();
    };
    document.addEventListener('mousedown', onDown, true);
    document.addEventListener('mousedown', handler);
    return () => {
      document.removeEventListener('mousedown', onDown, true);
      document.removeEventListener('mousedown', handler);
    };
  }, [editingId, exitEditing]);

  // ---- Drag-over for layer drops + reorder ----

  const canBeLayer = draggedBlockTier === 'layer' || draggedBlockTier === 'both';
  const anyLayerDrag = (isDragging && canBeLayer) || isReordering;
  // The layer-gap indicator also shows while dragging an element OUT of a column
  // (i.e. over a gap, not a cell) so the user can drop it as a new top-level block.
  const showLayerIndicator =
    (anyLayerDrag && columnDropTarget === null) ||
    (isElementDragging && elementDropTarget === null);

  const computeDropIndex = useCallback((e: React.DragEvent) => {
    const canvas = canvasRef.current;
    if (!canvas) return 0;
    const layerEls = canvas.querySelectorAll('[data-dnd-layer-idx]');
    if (layerEls.length === 0) return 0;
    const mouseY = e.clientY;
    let best = 0;
    layerEls.forEach((el) => {
      const rect = el.getBoundingClientRect();
      if (mouseY > rect.top + rect.height / 2) best = Number(el.getAttribute('data-dnd-layer-idx')) + 1;
    });
    return best;
  }, []);

  const handleCanvasDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = isReordering || isElementDragging ? 'move' : 'copy';

    if (isElementDragging) {
      // Over the canvas (a gap between layers), not a cell — offer a drag-out target.
      onDropIndexChange(computeDropIndex(e));
      onElementDropTargetChange(null);
      return;
    }
    if (isReordering || (isDragging && canBeLayer)) {
      onDropIndexChange(computeDropIndex(e));
      onColumnDropTargetChange(null);
    }
  }, [isDragging, isReordering, isElementDragging, canBeLayer, computeDropIndex, onDropIndexChange, onColumnDropTargetChange, onElementDropTargetChange]);

  const handleCanvasDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    if (isElementDragging) {
      if (elementDropTarget) onElementDrop();
      else onElementDragOut(dropTargetIndex ?? layers.length);
      return;
    }
    if (isReordering && reorderFromIndex !== null) {
      onReorderDrop(dropTargetIndex ?? reorderFromIndex);
      return;
    }
    onCanvasDrop();
  }, [isElementDragging, elementDropTarget, isReordering, reorderFromIndex, dropTargetIndex, layers.length, onElementDrop, onElementDragOut, onReorderDrop, onCanvasDrop]);

  const handleColumnDragOver = useCallback((_e: React.DragEvent, layerId: string, columnId: string) => {
    onColumnDropTargetChange({ layerId, columnId });
    onDropIndexChange(null);
  }, [onColumnDropTargetChange, onDropIndexChange]);

  // ---- Layer drag handle ----

  const handleLayerDragStart = useCallback((e: React.DragEvent, layerIndex: number, layerId: string) => {
    e.stopPropagation();
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', `reorder-${layerIndex}`);

    const layerEl = layerRefs.current.get(layerId);
    if (layerEl) {
      const rect = layerEl.getBoundingClientRect();
      const ghost = layerEl.cloneNode(true) as HTMLElement;
      ghost.className = 'dnd-reorder-ghost';
      ghost.style.width = `${rect.width}px`;
      ghost.style.position = 'fixed';
      ghost.style.top = '-2000px';
      document.body.appendChild(ghost);
      e.dataTransfer.setDragImage(ghost, rect.width / 2, rect.height / 2);
      requestAnimationFrame(() => document.body.removeChild(ghost));
    }

    onReorderStart(layerIndex);
  }, [onReorderStart]);

  const handleLayerDragEnd = useCallback(() => {
    onReorderEnd();
    onDropIndexChange(null);
  }, [onReorderEnd, onDropIndexChange]);

  // ---- Render ----

  const wrapperStyle: CSSProperties = {
    maxWidth: device === 'mobile' ? 375 : maxWidth,
    width: '100%',
    margin: '0 auto',
    backgroundColor: wrapper.bgColor || globalStyles.contentBgColor,
    padding: spacingToCss(wrapper.padding),
    transition: 'max-width 0.3s ease',
    boxShadow: device === 'mobile' ? '0 0 20px rgba(0,0,0,0.3)' : undefined,
  };

  return (
    <div
      ref={canvasRef}
      className={`dnd-canvas-scroll${isDragging || isReordering ? ' dnd-canvas-drop-active' : ''}`}
      onClick={handleCanvasClick}
      onDragOver={handleCanvasDragOver}
      onDrop={handleCanvasDrop}
      onDragLeave={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node)) {
          onDropIndexChange(null);
          onColumnDropTargetChange(null);
        }
      }}
      style={
        globalStyles.bodyBgColor
          // A real body color fully covers the transparency checkerboard.
          ? { backgroundColor: globalStyles.bodyBgColor, backgroundImage: 'none' }
          // Transparent body → keep the checkerboard from the CSS class.
          : undefined
      }
    >
      <div style={wrapperStyle}>
        {layers.length === 0 && !isDragging && (
          <div className="dnd-canvas-empty">
            <ArrowLeft size={32} />
            <p>Drag a layer here or click one to start building your email</p>
          </div>
        )}

        {layers.map((layer, idx) => {
          const isSelected = selection.layerId === layer.id;
          const isHovered = hoveredLayerId === layer.id && !isSelected;
          const isEditing = editingId === layer.id;
          const isBeingReordered = isReordering && reorderFromIndex === idx;
          const colPcts = layer.type === 'columns' ? getColumnWidths(layer as ColumnsLayer) : [];
          const colPxWidths = layer.type === 'columns' ? getColumnPixelWidths(layer as ColumnsLayer, maxWidth) : [];

          return (
            <div key={layer.id} data-dnd-layer-idx={idx}>
              {/* Drop indicator before this layer */}
              {showLayerIndicator && dropTargetIndex === idx && (
                <div className="dnd-drop-indicator-bar" />
              )}

              <div
                ref={(el) => { if (el) layerRefs.current.set(layer.id, el); else layerRefs.current.delete(layer.id); }}
                className={`dnd-layer-render${isHovered ? ' hovered' : ''}${isSelected ? ' selected' : ''}${isEditing ? ' editing' : ''}${isBeingReordered ? ' reordering' : ''}`}
                // Outer spacing renders as padding here — same as the compiled
                // email, where it is padding on a wrapping table cell.
                style={hasSpacing(layer.margin) ? { padding: spacingToCss(layer.margin) } : undefined}
                onMouseEnter={() => { if (!isDragging && !isReordering) setHoveredLayerId(layer.id); }}
                onMouseLeave={() => setHoveredLayerId(null)}
                onClick={(e) => handleLayerClick(e, layer.id)}
                onDoubleClick={(e) => {
                  e.stopPropagation();
                  handleDblClickLayer(layer, { x: e.clientX, y: e.clientY });
                }}
              >
                {/* Drag handle on left side */}
                {isSelected && !editingId && (
                  <div
                    className="dnd-layer-drag-handle"
                    draggable
                    onDragStart={(e) => handleLayerDragStart(e, idx, layer.id)}
                    onDragEnd={handleLayerDragEnd}
                    onMouseDown={(e) => e.stopPropagation()}
                    title="Drag to reorder"
                  >
                    <GripVertical size={14} />
                  </div>
                )}

                {/* Layer toolbar — vertical, on the right so it never collides
                    with an element's own top toolbar inside a columns layer. */}
                {isSelected && !editingId && (
                  <div className="dnd-layer-toolbar" onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}>
                    {(layer.type === 'text' || layer.type === 'image') && (
                      <button
                        title={layer.locked ? 'Locked for editors — click to unlock' : 'Unlocked for editors — click to lock'}
                        className={layer.locked ? undefined : 'dnd-toolbar-unlocked'}
                        onClick={(e) => { e.stopPropagation(); onUpdateLayer(layer.id, { locked: !layer.locked } as Partial<Layer>); }}
                      >
                        {layer.locked ? <Lock size={13} /> : <LockOpen size={13} />}
                      </button>
                    )}
                    {layer.type === 'text' && (
                      <button
                        title="Edit text"
                        onClick={(e) => { e.stopPropagation(); handleDblClickLayer(layer, null); }}
                      >
                        <Pencil size={13} />
                      </button>
                    )}
                    {layer.type === 'image' && (
                      <button
                        title="Change image"
                        onClick={(e) => { e.stopPropagation(); handleDblClickLayer(layer, null); }}
                      >
                        <ImageIcon size={13} />
                      </button>
                    )}
                    {layerHasEditModal(layer.type) && (
                      <button
                        title={layer.type === 'columns' ? 'Edit column layout' : 'Edit social links'}
                        onClick={(e) => { e.stopPropagation(); handleEditLayer(layer); }}
                      >
                        <Pencil size={13} />
                      </button>
                    )}
                    <button title="Duplicate" onClick={(e) => { e.stopPropagation(); handleLayerToolbarAction('duplicate', layer.id, idx); }}>
                      <Copy size={13} />
                    </button>
                    <button title="Delete" className="dnd-action-delete-btn" onClick={(e) => { e.stopPropagation(); handleLayerToolbarAction('delete', layer.id, idx); }}>
                      <Trash2 size={13} />
                    </button>
                  </div>
                )}

                {/* Layer content */}
                {isEditing && layer.type === 'text' ? (
                  <InlineTextEditor
                    initialHtml={(layer as TextLayer).content}
                    nodeType="text"
                    align={(layer as TextLayer).textAlign}
                    editableStyle={{
                      ...layerBaseStyle(layer),
                      fontFamily: globalStyles.fontFamily,
                      fontSize: globalStyles.fontSize,
                      color: globalStyles.textColor,
                      lineHeight: globalStyles.lineHeight,
                      textAlign: layer.type === 'text' ? (layer as TextLayer).textAlign : undefined,
                      wordBreak: 'break-word',
                      outline: 'none',
                      minHeight: '1em',
                    }}
                    openAtPoint={editOpenPoint}
                    linkColor={globalStyles.linkColor}
                    onInput={(html) => onUpdateLayer(layer.id, { content: html } as Partial<Layer>)}
                    onAlignChange={(a) => onUpdateLayer(layer.id, { textAlign: a } as Partial<Layer>)}
                    onExit={exitEditing}
                    onOpenLinkModal={onOpenLinkModal}
                    onOpenVariableModal={onOpenVariableModal}
                  />
                ) : layer.type === 'columns' ? (() => {
                  const clayer = layer as ColumnsLayer;
                  const mobileStack = device === 'mobile' && clayer.stackOnMobile !== false;
                  const indices = clayer.columns.map((_, i) => i);
                  const order = mobileStack && clayer.reverseStackOnMobile ? [...indices].reverse() : indices;
                  const renderCell = (ci: number) => (
                    <ColumnCell
                      key={clayer.columns[ci].id}
                      col={clayer.columns[ci]}
                      colWidthPct={mobileStack ? 100 : (colPcts[ci] ?? colPcts[0])}
                      colPxWidth={mobileStack ? maxWidth : (colPxWidths[ci] ?? colPxWidths[0])}
                      gapRight={!mobileStack && ci < clayer.columns.length - 1 ? clayer.columnsGap : 0}
                      layerId={layer.id}
                      gs={globalStyles}
                      selection={selection}
                      hoveredElementId={hoveredElementId}
                      editingElementId={editingId}
                      editOpenPoint={editOpenPoint}
                      onHoverElement={setHoveredElementId}
                      onClickElement={handleElementClick}
                      onDblClickElement={handleDblClickElement}
                      onElementToolbarAction={handleElementToolbarAction}
                      onSelectColumn={handleColumnClick}
                      onUpdateElement={onUpdateElement}
                      onExitEditing={exitEditing}
                      onOpenLinkModal={onOpenLinkModal}
                      onOpenVariableModal={onOpenVariableModal}
                      isDragging={isDragging}
                      isReordering={isReordering}
                      draggedBlockTier={draggedBlockTier}
                      columnDropTarget={columnDropTarget}
                      onColumnDragOver={handleColumnDragOver}
                      onColumnDragLeave={() => onColumnDropTargetChange(null)}
                      isElementDragging={isElementDragging}
                      elementDropTarget={elementDropTarget}
                      onElementDragStart={onElementDragStart}
                      onElementDragEnd={onElementDragEnd}
                      onElementDropTargetChange={onElementDropTargetChange}
                      onElementDrop={onElementDrop}
                      onAddBlockToColumn={onAddBlockToColumn}
                    />
                  );
                  return (
                    <div style={layerBaseStyle(layer)}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: mobileStack ? 'auto' : 'fixed' }}>
                        <tbody>
                          {mobileStack
                            ? order.map((ci) => <tr key={clayer.columns[ci].id}>{renderCell(ci)}</tr>)
                            : <tr>{order.map((ci) => renderCell(ci))}</tr>}
                        </tbody>
                      </table>
                    </div>
                  );
                })() : (
                  <>
                    {layer.type === 'text' && <RenderTextLayer layer={layer} gs={globalStyles} />}
                    {layer.type === 'image' && <RenderImageLayer layer={layer} maxWidth={maxWidth} />}
                    {layer.type === 'button' && <RenderButtonLayer layer={layer} gs={globalStyles} />}
                    {layer.type === 'divider' && <RenderDividerLayer layer={layer} />}
                    {layer.type === 'spacer' && <RenderSpacerLayer layer={layer} />}
                    {layer.type === 'html' && <RenderHtmlLayer layer={layer} />}
                    {layer.type === 'social' && <RenderSocialLayer layer={layer as SocialLayer} />}
                    {layer.type === 'video' && <RenderVideoLayer layer={layer as VideoLayer} maxWidth={maxWidth} />}
                    {layer.type === 'countdown' && <RenderCountdownLayer layer={layer as CountdownLayer} />}
                  </>
                )}
              </div>

              {/* Drop indicator after last layer */}
              {showLayerIndicator && dropTargetIndex === idx + 1 && idx === layers.length - 1 && (
                <div className="dnd-drop-indicator-bar" />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
